import { Menu, type MenuItem } from "../../components/ui/Menu";
import { useI18n } from "../../i18n/I18nProvider";
import type { Member, Role } from "../../lib/api-types";

export interface MemberRowProps {
  member: Member;
  isOwner: boolean;
  isSelf: boolean;
  busy: boolean;
  onRoleChange: (role: Role) => void;
  onRemove: () => void;
}

/** Ink for the owner, ice for members: condensed caps, so the role reads at a glance. */
export function RoleBadge({ owner }: { owner: boolean }) {
  const { t } = useI18n();
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-control px-2 py-0.5 font-display text-micro font-extrabold tracking-[0.06em] uppercase ${
        owner ? "bg-ink text-board" : "bg-ice-2 text-ink-2"
      }`}
    >
      {t(owner ? "common.role.owner" : "common.role.member")}
    </span>
  );
}

/** One member as a label-grid row (owners see it). The owner row is protected: no menu. Remove is not offered for yourself. */
export function MemberRow({
  member,
  isOwner,
  isSelf,
  busy,
  onRoleChange,
  onRemove,
}: MemberRowProps) {
  const { t } = useI18n();
  const label = member.name || member.email;
  const items: MenuItem[] = [
    { label: t("members.makeOwner"), onSelect: () => onRoleChange("owner"), disabled: busy },
    ...(isSelf
      ? []
      : [
          {
            label: t("common.remove"),
            onSelect: onRemove,
            tone: "danger" as const,
            disabled: busy,
          },
        ]),
  ];
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-operate font-semibold text-ink">
          {label}
          {isSelf ? (
            <span className="ml-2 text-note font-medium text-ink-3">({t("members.you")})</span>
          ) : null}
        </p>
        {member.name ? <p className="truncate text-note text-ink-2">{member.email}</p> : null}
      </div>
      <RoleBadge owner={isOwner} />
      {isOwner ? (
        <span aria-hidden="true" className="size-11 shrink-0" />
      ) : (
        <Menu label={t("members.moreActions", { name: label })} items={items} />
      )}
    </li>
  );
}
